import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type {
  TGuaranteesApplicationTenderInfo,
  TGuaranteesProzorroTender,
} from '~/store/guarantees/types.ts';
import {
  areGuaranteesTenderLotIdsEqual,
  getGuaranteesSelectedTenderLotIds,
  hasGuaranteesTenderRawLots,
  parseGuaranteesTenderDetailsFromRawFeedJson,
} from '../../../utils/tender/utils.ts';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';
import FlowSection from '../shared/FlowSection.tsx';
import TenderLotsList from './TenderLotsList.tsx';
import TenderSearchRow from './TenderSearchRow.tsx';
import TenderSummary from './TenderSummary.tsx';

interface IProzorroTenderSectionProps {
  displayedTenderPublicId: string;
  savedTenderPublicId: string;
  isSavedProzorroTender: boolean;
  isSearchingTender: boolean;
  isSelectingTender: boolean;
  prozorroTender: TGuaranteesProzorroTender | null;
  applicationTenderInfo: TGuaranteesApplicationTenderInfo | null;
  onTenderSearch: (tenderPublicId: string) => Promise<void>;
  onTenderSelect: (selectedLotIds: string[]) => Promise<boolean>;
  onTenderSelectionChange: (hasChanges: boolean) => void;
  isTenderSelectionRequired: boolean;
}

const ProzorroTenderSection: React.FC<IProzorroTenderSectionProps> = ({
  displayedTenderPublicId,
  savedTenderPublicId,
  isSavedProzorroTender,
  isSearchingTender,
  isSelectingTender,
  prozorroTender,
  applicationTenderInfo,
  onTenderSearch,
  onTenderSelect,
  onTenderSelectionChange,
  isTenderSelectionRequired,
}) => {
  const { isReadOnly } = useGuaranteesRootApplicationSectionContext();
  const [selectedTenderLotIds, setSelectedTenderLotIds] = React.useState<string[]>([]);
  const tenderDetails = React.useMemo(
    () => parseGuaranteesTenderDetailsFromRawFeedJson(prozorroTender?.rawFeedJson),
    [prozorroTender?.rawFeedJson]
  );
  const hasTenderLots = React.useMemo(
    () => hasGuaranteesTenderRawLots(prozorroTender?.rawFeedJson),
    [prozorroTender?.rawFeedJson]
  );
  const tenderLots = hasTenderLots ? (tenderDetails?.lots ?? []) : [];
  const savedTenderLotIds = React.useMemo(
    () => getGuaranteesSelectedTenderLotIds(applicationTenderInfo),
    [applicationTenderInfo]
  );
  const currentTenderPublicId = String(prozorroTender?.tenderPublicId ?? '').trim();
  const isTenderChanged =
    !!currentTenderPublicId && currentTenderPublicId !== savedTenderPublicId.trim();
  const hasTenderSelectionChanges =
    isTenderChanged || !areGuaranteesTenderLotIdsEqual(selectedTenderLotIds, savedTenderLotIds);
  const isTenderSelectDisabled =
    !prozorroTender?.id ||
    !prozorroTender?.tenderPublicId ||
    (hasTenderLots && !selectedTenderLotIds.length) ||
    (!hasTenderSelectionChanges && !isTenderSelectionRequired) ||
    isSelectingTender ||
    isReadOnly;

  const toggleLot = (lotId: string) => {
    setSelectedTenderLotIds((currentIds) =>
      currentIds.includes(lotId)
        ? currentIds.filter((selectedLotId) => selectedLotId !== lotId)
        : [...currentIds, lotId]
    );
  };

  const handleTenderSearch = async (tenderPublicId: string) => {
    setSelectedTenderLotIds([]);
    await onTenderSearch(tenderPublicId);
  };

  React.useEffect(() => {
    setSelectedTenderLotIds(savedTenderLotIds);
  }, [savedTenderLotIds]);

  React.useEffect(() => {
    if (!applicationTenderInfo) {
      setSelectedTenderLotIds([]);
    }
  }, [applicationTenderInfo, prozorroTender?.id]);

  React.useEffect(() => {
    onTenderSelectionChange(hasTenderSelectionChanges);
  }, [hasTenderSelectionChanges, onTenderSelectionChange]);

  return (
    <FlowSection
      step={guaranteesClientText.prozorroSearchStepLabel}
      title={guaranteesClientText.prozorroSearchTitle}
      description={guaranteesClientText.prozorroSearchDescription}
      disabled={!isSavedProzorroTender}
      required={isSavedProzorroTender}
    >
      <TenderSearchRow
        disabled={!isSavedProzorroTender || isReadOnly}
        isSearchingTender={isSearchingTender}
        isSelectingTender={isSelectingTender}
        onTenderSearch={handleTenderSearch}
      />

      {isSavedProzorroTender && !!displayedTenderPublicId && (
        <>
          <TenderSummary
            displayedTenderPublicId={displayedTenderPublicId}
            prozorroTender={prozorroTender}
            tenderDetails={tenderDetails}
          />

          {!!tenderLots.length && (
            <TenderLotsList
              isSelectingTender={isSelectingTender}
              isReadOnly={isReadOnly}
              lots={tenderLots}
              selectedTenderLotIds={selectedTenderLotIds}
              onSelectedTenderLotToggle={toggleLot}
            />
          )}

          {!tenderLots.length && !!prozorroTender?.id && (
            <Typography sx={guaranteesClientPageStyles.flowLockHint}>
              {guaranteesClientText.emptyLots}
            </Typography>
          )}

          {!selectedTenderLotIds.length && !!tenderLots.length && (
            <Typography sx={guaranteesClientPageStyles.flowLockHint}>
              {guaranteesClientText.selectLotBeforeTenderBindHint}
            </Typography>
          )}

          <Box sx={guaranteesClientPageStyles.flowActions}>
            <Button
              type="button"
              variant="contained"
              disabled={isTenderSelectDisabled}
              startIcon={isSelectingTender ? <ButtonLoadingIcon /> : undefined}
              onClick={() => void onTenderSelect(selectedTenderLotIds)}
              sx={dataFormStyles.documentPrimaryAction}
            >
              {guaranteesClientText.selectButton}
            </Button>
          </Box>
        </>
      )}
    </FlowSection>
  );
};

export default ProzorroTenderSection;
